import { z } from '@/i18n';
import { useCountdownTimer } from '@/hooks/use-countdown-timer';
import { Validators } from '@/lib/validator';
import { useAuthStore } from '@/store/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormError, FormField, FormLabel, FormLabelGroup, Input, Link } from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from 'oidc-client-ts';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

interface EmailOtpVerifyProps {
  userCredentials: { username: string; password: string };
  onVerifySuccess: () => void;
  onVerifyError: (error: ErrorResponse) => void;
  redirectUrl?: string;
}

const otpFormSchema = z.object({
  otp: z
    .string()
    .length(6)
    .refine(...Validators.stringIsNumber),
});

const RESEND_OTP_TIMEOUT = 120;

export const EmailOtpVerify = ({
  userCredentials,
  onVerifySuccess,
  onVerifyError,
  redirectUrl,
}: EmailOtpVerifyProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authService = useAuthStore((state) => state.authService);

  // Use the custom hook instead of manual timer state and useEffect
  const { formatTime, isFinished, startTimer } = useCountdownTimer(RESEND_OTP_TIMEOUT);
  const resendDisabled = !isFinished;

  const { mutate: verifyOtp, isPending: isVerifyLoading } = useMutation({
    mutationFn: async (data: { email: string; otp: string }) => {
      try {
        await authService!.signinWithOtp(data.email, data.otp);
        onVerifySuccess();
      } catch (err) {
        const errorResponse = err as ErrorResponse;

        // Check if the error is "Account is not fully set up"
        if (
          errorResponse.error === 'invalid_grant' &&
          errorResponse.error_description === 'Account is not fully set up'
        ) {
          // Navigate to verify-email page with the email
          navigate({
            to: '/verify-email',
            search: {
              email: data.email,
              redirect: redirectUrl,
            },
          });
          return;
        }

        onVerifyError(errorResponse);
      }
    },
  });

  const { mutate: resendVerification, isPending: isResendVerificationLoading } = useMutation({
    mutationFn: async (data: { username: string; password: string }) => {
      try {
        await authService!.signinResourceOwnerCredentials(data.username, data.password);
      } catch (err) {
        onVerifyError(err as ErrorResponse);
      }
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
  } = useForm<{ otp: string }>({
    values: { otp: '' },
    resolver: zodResolver(otpFormSchema),
  });

  const { field: otpField } = useController({
    name: 'otp',
    control,
    disabled: isVerifyLoading || isResendVerificationLoading,
  });

  const otpValue = watch('otp');

  const handleResend = async () => {
    startTimer();
    await resendVerification({ username: userCredentials.username, password: userCredentials.password });
  };

  const onSubmit: SubmitHandler<{ otp: string }> = async ({ otp }) => {
    verifyOtp({ email: userCredentials.username, otp });
  };

  return (
    <div className="flex flex-col gap-10">
      <div>
        <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('login.otp.title')}</h1>
        <p className="text-xl text-muted-foreground">{t('login.verify.description')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-10">
          <div className="mb-4">
            <FormLabel>{t('login.authMethods.email.fields.email.label')}</FormLabel>
            <div className="text-base">{userCredentials.username}</div>
          </div>

          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!errors.otp}>{t('login.otp.verificationCode')}</FormLabel>

              {errors.otp && <FormError>{errors.otp.message}</FormError>}
            </FormLabelGroup>

            <Input
              disabled={otpField.disabled}
              error={!!errors.otp}
              value={otpField.value}
              onChange={(event) => {
                const value = event.target.value;

                if (!value || value.match(/^\d+$/)) {
                  otpField.onChange(event);
                } else {
                  event.preventDefault();
                }
              }}
              maxLength={6}
              autoComplete="one-time-code"
            />
          </FormField>

          <div className="flex flex-col items-start gap-2">
            <span className="font-medium">{!isFinished ? formatTime() : ''}</span>

            <span>
              {t('login.otp.notReceived')}{' '}
              <Link type="button" onClick={handleResend} disabled={resendDisabled}>
                {t('login.otp.resend')}
              </Link>
            </span>
          </div>
        </div>

        <Button
          className="w-full my-10"
          type="submit"
          disabled={isVerifyLoading || otpValue.length !== 6 || isResendVerificationLoading}
        >
          {t('login.otp.login')}
        </Button>
      </form>
    </div>
  );
};
